import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Route, NavLink, useLocation, useParams, Redirect, Switch } from 'react-router-dom';
import { DateTime } from 'luxon';

import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Divider from '@material-ui/core/Divider';
import Badge from '@material-ui/core/Badge';

import QrIcon from '@material-ui/icons/CropFree';

import General from 'app/components/event/General';
import Invitations from 'app/components/event/Invitations';
import Tickets from 'app/components/event/Tickets';
import Settings from 'app/components/event/Settings';
import BulkInvitations from 'app/components/event/BulkInvitations';
import EditInvitation from 'app/pages/admin/EditInvitation';
import QrScannerDialog from 'app/components/event/QrScannerDialog';

import Loading from 'app/components/loaders/Loading';

import useApi from 'app/context/useApi';

import { container} from 'app/styles/new-layout'
import { paper } from 'app/styles/core';

const useStyles = makeStyles(theme => ({
    container: {
        ...container(theme),
        padding: theme.spacing(3),
        minHeight: '80vh',
        position: 'relative'
    },
    paper: {
        ...paper(theme)
    },
    header: {
        padding: theme.spacing(3)
    },
    badge: {
        top: '-5px'
    }
}));

export default function EditEvent() {

    let location = useLocation();
    let params = useParams();
    const api = useApi();

    const [ event, setEvent ] = useState(null);
    const [ loading, setLoading ] = useState(false);
    const [ groups, setGroups ] = useState(null);

    const [ scannerDialogOpen, setScannerDialogOpen ] = useState(false);

    const classes = useStyles();
    const eventID = params.id

    const pathname = location.pathname;

    // Getters ------------------------------------------

    const getEvent = ($quiet = false) => {
        if (! $quiet) setLoading(true);

        api.get(`/admin/event/${eventID}`)
            .then(res => {
                setEvent({
                    ...res.event,
                    published_at: DateTime.fromISO(res.event.published_at),
                    starts_at: DateTime.fromISO(res.event.starts_at),
                    ends_at: DateTime.fromISO(res.event.ends_at),
                    created_at: DateTime.fromISO(res.event.created_at),
                });
            })
            .finally(() => setLoading(false))
        }

    const getGroups = () => {
        setLoading(true);
        api.get('/admin/group')
            .then(res => setGroups(res.groups))
    }

    // Effects ------------------------------------------

    useEffect(() => {
        getEvent();
        getGroups();
    }, []);

    // Handlers ---------------------------------------------------

    // Posters ------------------------------------------------

    // Scanner Dialog Functions ---------------------------------------------------

    const openScannerDialog = () => {
        setScannerDialogOpen(true)
    }

    const closeScannerDialog = () => {
        setScannerDialogOpen(false)
    }

    // Constants ---------------------------------------------------

    const isSelected = tab => (pathname.includes(tab));

    return (
        <div className={classes.container}>
            <Loading isLoading={loading}/>
            {(event && groups) &&
                <Paper>
                    <div className={classes.header}>
                        <Grid container spacing={3} justify="space-between" alignItems="center">
                            <Grid item>
                                <Badge
                                    color={event.published ? 'primary' : 'error'}
                                    badgeContent={event.published ? 'PUBLISHED' : 'DRAFT'}
                                    classes={{
                                        badge: classes.badge
                                    }}
                                    >
                                    <Typography color="primary" variant="h4">
                                        {event.name}
                                    </Typography>
                                </Badge>
                            </Grid>
                            <Grid item>
                                <ButtonGroup color="primary" aria-label="event navigation">
                                    <Button
                                        to={`/admin/events/${eventID}/general`}
                                        variant={isSelected(`/admin/events/${eventID}/general`) ? 'contained' : 'outlined'}
                                        component={NavLink}
                                        >
                                        General
                                    </Button>
                                    <Button
                                        to={`/admin/events/${eventID}/invitations`}
                                        variant={isSelected(`/admin/events/${eventID}/invitations`) ? 'contained' : 'outlined'}
                                        component={NavLink}
                                        >
                                        Invitations
                                    </Button>
                                    <Button
                                        to={`/admin/events/${eventID}/tickets`}
                                        variant={isSelected(`/admin/events/${eventID}/tickets`) ? 'contained' : 'outlined'}
                                        component={NavLink}
                                        >
                                        Tickets
                                    </Button>
                                    <Button
                                        to={`/admin/events/${eventID}/settings`}
                                        variant={isSelected(`/admin/events/${eventID}/settings`) ? 'contained' : 'outlined'}
                                        component={NavLink}
                                        >
                                        Settings
                                    </Button>
                                </ButtonGroup>
                            </Grid>
                            <Grid item>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={openScannerDialog}
                                    startIcon={<QrIcon/>}
                                    >
                                    Start Scanning
                                </Button>
                            </Grid>
                        </Grid>
                    </div>
                    <Divider />
                    <Switch>
                        <Route path={`/admin/events/${eventID}/general`} exact>
                            <General
                                event={event}
                                tickets={event.tickets}
                                groups={groups}
                                loading={loading}
                                setLoading={setLoading}
                            />
                        </Route>
                        <Route path={`/admin/events/${eventID}/invitations`} exact>
                            <Invitations
                                event={event}
                                tickets={event.tickets}
                                groups={groups}
                                eventID={eventID}
                                getEvent={getEvent}
                                loading={loading}
                                setLoading={setLoading}
                            />
                        </Route>
                        <Route path={`/admin/events/${eventID}/tickets`} exact>
                            <Tickets
                                event={event}
                                tickets={event.tickets}
                                getEvent={getEvent}
                                setLoading={setLoading}
                            />
                        </Route>
                        <Route path={`/admin/events/${eventID}/settings`}>
                            <Settings
                                event={event}
                                groups={groups}
                                getEvent={getEvent}
                                tickets={event.tickets}
                                loading={loading}
                                setLoading={setLoading}
                            />
                        </Route>
                        <Route path={`/admin/events/${eventID}/invitations/:id`} exact>
                            <EditInvitation event={event} getEvent={getEvent}/>
                        </Route>
                        <Route path={`/admin/events/${eventID}/bulk-upload`}>
                            <BulkInvitations
                                event={event}
                                groups={groups}
                                getEvent={getEvent}
                                tickets={event.tickets}
                                loading={loading}
                                setLoading={setLoading}
                            />
                        </Route>
                        <Redirect from={`/admin/events/${eventID}`} to={`/admin/events/${eventID}/general`}/>
                        <Redirect from={`/admin/events/${eventID}/*`} to={`/admin/events/${eventID}/general`}/>
                    </Switch>
                    <QrScannerDialog
                        open={scannerDialogOpen}
                        onClose={closeScannerDialog}
                        event={event}
                    />
                </Paper>
            }
        </div>
    );
}
